<template>
  <div class="form-group">
    <label class="font-size-12">{{ label }}</label>
    <div :id="`icon-picker-wrap_${id}`" class="icon-picker-wrap">
      <div class="icon-picker-wrapHeader">
        <template v-if="value">
          <label :data-id="id" @click="toggleBox">
            <FaIcon :icon="value" class="icon-picker-selected" />
            <span>{{ value }}</span>
          </label>
          <i :class="xMarkIcon" :data-id="id" @click="clearIcon" />
        </template>
        <template v-else>
          <label :data-id="id" @click="toggleBox"><i :class="magnifyingIcon" /></label>
        </template>
      </div>
      <div v-if="showBox" class="icon-picker-modal">
        <div class="icon-picker-modalHeader" :data-id="id">
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            class="form-control"
            placeholder="Search icons..."
            :data-id="id"
          />
          <p v-if="resultSummary" class="icon-picker-hint">{{ resultSummary }}</p>
        </div>
        <ul>
          <li
            v-for="icon in visibleIcons"
            :key="icon"
            :data-id="id"
            :title="icon"
            @click="clickIcon(icon)"
          >
            <FaIcon :icon="icon" :title="icon" class="icon-picker-glyph" />
          </li>
        </ul>
        <div v-if="hasMore" class="icon-picker-more" :data-id="id">
          <button type="button" class="btn btn-sm btn-light" :data-id="id" @click="loadMore">
            Show more
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './Styles/IconPicker.scss'
import FontAwesomeIconsObject from './FontAwesomeIconsObject'
import FaIcon from './FaIcon.vue'
import { MAGNIFYING_ICON, XMARK_ICON } from '@src/icons'

const PAGE_SIZE = 80

export default {
  components: { FaIcon },
  props: {
    id: { type: String, default: null, required: true },
    label: { type: String, default: '' },
    value: { type: [Number, String], default: '' }
  },
  data() {
    return {
      magnifyingIcon: MAGNIFYING_ICON,
      xMarkIcon: XMARK_ICON,
      faIcons: Object.keys(FontAwesomeIconsObject),
      showBox: false,
      search: '',
      listening: false,
      visibleCount: PAGE_SIZE
    }
  },
  computed: {
    matchedIcons() {
      const term = (this.search || '').trim().toLowerCase()
      if (!term) return this.faIcons
      return this.faIcons.filter(icon => this.getIconName(icon).toLowerCase().includes(term))
    },
    visibleIcons() {
      return this.matchedIcons.slice(0, this.visibleCount)
    },
    hasMore() {
      return this.matchedIcons.length > this.visibleCount
    },
    resultSummary() {
      const total = this.matchedIcons.length
      if (!total) return 'No icons found'
      if (total <= this.visibleCount) return `${total} icon${total === 1 ? '' : 's'}`
      return `Showing ${this.visibleCount} of ${total} — refine search or show more`
    }
  },
  watch: {
    search() {
      this.visibleCount = PAGE_SIZE
    }
  },
  beforeDestroy() {
    this.removeListener()
  },
  methods: {
    addListener() {
      if (this.listening) return
      document.body.addEventListener('click', this.manageIconPicker)
      this.listening = true
    },
    removeListener() {
      if (!this.listening) return
      document.body.removeEventListener('click', this.manageIconPicker)
      this.listening = false
    },
    manageIconPicker(e) {
      if (this.showBox && this.$el && !this.$el.contains(e.target)) {
        this.hideBox()
      }
    },
    hideBox() {
      this.showBox = false
      this.search = ''
      this.visibleCount = PAGE_SIZE
      this.removeListener()
    },
    toggleBox() {
      this.showBox = !this.showBox
      if (!this.showBox) {
        this.hideBox()
      } else {
        this.$nextTick(() => {
          this.addListener()
          if (this.$refs.searchInput) this.$refs.searchInput.focus()
        })
      }
    },
    loadMore() {
      this.visibleCount += PAGE_SIZE
    },
    getIconName(string) {
      if (string.split(' ').length > 1 && string.split(' ')[0] && string.split(' ')[1]) {
        return string.split(' ')[1]
      }
      return string
    },
    clickIcon(string) {
      this.$emit('input', string)
      this.$emit('change', this)
      this.hideBox()
    },
    clearIcon() {
      this.$emit('input', '')
      this.$emit('change', this)
      this.hideBox()
    }
  }
}
</script>
